import { createLogger } from '@svenskaspelab/nova-core/log';

import type { Draw } from '../../../../schema/draw/entry.js';
import type { DrawForecast, DrawResult } from '../../../../schema/forecast/entry.js';
import {
  type IGetDrawForecastUseCase,
  type IGetDrawResultUseCase,
  type IGetDrawUseCase,
  NotFoundError,
} from '../../../domain/entry.js';
import type { DrawQueryOptions } from '../../../lib/entry.js';

const logger = createLogger('poolbetting.draw-result-controller');

/** How long after cancelCloseTime a missing forecast is still considered an expected lazy-generation delay. */
const FORECAST_GRACE_PERIOD_MS = 30_000;

export type IGetDrawResultController = ReturnType<typeof getDrawResultController>;

// When draw is pre live there is no forecast or result, therefore the type is needed so tanstack query won't error out
export type ForecastData =
  { type: 'FORECAST'; data: DrawForecast } | { type: 'RESULT'; data: DrawResult } | { type: 'NONE' };

/**
 * Logs a missing forecast for a live draw, downgrading to debug while the backend is still expected to generate it.
 *
 * @param error The caught NotFoundError
 * @param cancelCloseTime The draw cancel close time, if known
 * @param queryInfo The query used to fetch the forecast
 */
function logMissingLiveForecast(
  error: unknown,
  cancelCloseTime: Draw['cancelCloseTime'] | undefined,
  queryInfo: DrawQueryOptions
): void {
  const msSinceCancelClose = cancelCloseTime
    ? Date.now() - new Date(cancelCloseTime).getTime()
    : Number.POSITIVE_INFINITY;

  if (msSinceCancelClose < FORECAST_GRACE_PERIOD_MS) {
    logger.debug('Draw forecast not generated yet', { cancelCloseTime, queryInfo });

    return;
  }

  logger.error('Draw forecast still missing after grace period', {
    error,
    cancelCloseTime,
    msSinceCancelClose,
    queryInfo,
  });
}

/**
 * Controller to get a specific draw for a product.
 *
 * @param getDrawResultUseCase Use case to get a draw result
 * @param getDrawForecastUseCase use case to get a draw forecast
 * @param getDrawUseCase Use case to get a specific draw
 * @returns Controller executor function to get draw forecast or result
 */
export function getDrawResultController(
  getDrawResultUseCase: IGetDrawResultUseCase,
  getDrawForecastUseCase: IGetDrawForecastUseCase,
  getDrawUseCase: IGetDrawUseCase
) {
  return async function controller(queryInfo: DrawQueryOptions): Promise<ForecastData> {
    let drawStatus: Draw['drawState']['client'] | undefined = undefined;
    let cancelCloseTime: Draw['cancelCloseTime'] | undefined = undefined;
    try {
      const drawData = await getDrawUseCase.execute(queryInfo);
      drawStatus = drawData.draw.drawState.client;
      cancelCloseTime = drawData.draw.cancelCloseTime;

      if (drawData.draw.drawState.client === 'Finished') {
        const data = await getDrawResultUseCase.execute(queryInfo);

        return { type: 'RESULT', data };
      }

      if (drawData.draw.drawState.client === 'Live') {
        const data = await getDrawForecastUseCase.execute(queryInfo);

        return { type: 'FORECAST', data };
      }
    } catch (error) {
      if (error instanceof NotFoundError) {
        // The backend generates the forecast lazily, so a 404 shortly after cancelCloseTime is expected.
        if (drawStatus === 'Live') {
          logMissingLiveForecast(error, cancelCloseTime, queryInfo);

          return { type: 'NONE' };
        }

        logger.error('Draw result not found', { error, drawStatus, queryInfo });
      }

      throw error;
    }

    return { type: 'NONE' };
  };
}
